import React, { useState } from 'react'
import { BriefingPack } from '../types'
import {
  ChevronDown,
  ChevronUp,
  Sparkles,
  AlertTriangle,
  ThumbsUp,
  ThumbsDown,
  Users,
  ClipboardList,
  User,
  Info
} from 'lucide-react'

interface Props {
  briefingPack: BriefingPack
  onApprove: () => void
  onStartOver: () => void
}

export default function ReviewStep({ briefingPack, onApprove, onStartOver }: Props) {
  return (
    <div className="space-y-6">
      {/* Important notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-blue-900 font-medium">
            This summary is generated from your uploaded document. It is not a clinical interpretation.
          </p>
          <p className="text-sm text-blue-800 mt-1">
            Please read and confirm this briefing reflects what your report says before sharing with your nanny.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-emerald-50 px-6 py-5 border-b border-emerald-100">
          <h2 className="text-lg font-semibold text-gray-900">
            Review Your Briefing Pack
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            AI can make mistakes: please review the generated content before approving. All information is extracted
            directly from the reports and your notes.
          </p>
        </div>

        <div className="divide-y divide-gray-200">
          {/* Snapshot */}
          <CollapsibleSection
            icon={<User className="w-5 h-5" />}
            title={`Snapshot of ${briefingPack.childName}`}
            defaultOpen
          >
            <p className="text-gray-700 whitespace-pre-wrap">{briefingPack.snapshot}</p>
          </CollapsibleSection>

          {/* Strengths */}
          {briefingPack.strengths.length > 0 && (
            <CollapsibleSection
              icon={<Sparkles className="w-5 h-5" />}
              title="Strengths"
              defaultOpen
            >
              <ul className="space-y-3">
                {briefingPack.strengths.map((item, index) => (
                  <BriefingListItem key={index} item={item} />
                ))}
              </ul>
            </CollapsibleSection>
          )}

          {/* Challenges */}
          <CollapsibleSection
            icon={<AlertTriangle className="w-5 h-5" />}
            title="Challenges, triggers and tough spots"
          >
            <ul className="space-y-3">
              {briefingPack.challenges.map((item, index) => (
                <BriefingListItem key={index} item={item} />
              ))}
            </ul>
          </CollapsibleSection>

          {/* What Helps */}
          <CollapsibleSection
            icon={<ThumbsUp className="w-5 h-5" />}
            title="What usually helps"
          >
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-4">
              <p className="text-sm text-blue-800">
                These are ideas for discussion with the family. Every child is different, and these
                suggestions should be explored together with the parents.
              </p>
            </div>
            <ul className="space-y-3">
              {briefingPack.whatHelps.map((item, index) => (
                <BriefingListItem key={index} item={item} />
              ))}
            </ul>
          </CollapsibleSection>

          {/* What Makes Things Worse */}
          <CollapsibleSection
            icon={<ThumbsDown className="w-5 h-5" />}
            title="What usually makes things worse"
          >
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-4">
              <p className="text-sm text-blue-800">
                These are patterns to be aware of and discuss with the family. Context matters,
                and the parents will have the best insight.
              </p>
            </div>
            <ul className="space-y-3">
              {briefingPack.whatMakesWorse.map((item, index) => (
                <BriefingListItem key={index} item={item} />
              ))}
            </ul>
          </CollapsibleSection>

          {/* Fit Check */}
          <CollapsibleSection
            icon={<Users className="w-5 h-5" />}
            title="Fit check for nannies"
          >
            <p className="text-gray-700 mb-4">
              Take a moment to reflect on whether this role is a good fit for your skills and experience:
            </p>
            <ul className="space-y-3">
              {briefingPack.fitCheck.map((prompt, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-medium text-emerald-700">{index + 1}</span>
                  </div>
                  <p className="text-gray-700">{prompt}</p>
                </li>
              ))}
            </ul>
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>A note on continuity:</strong> Children with neurodevelopmental differences
                often find changes in caregivers particularly challenging. Consistency and reliability
                are especially important for building trust and maintaining stability.
              </p>
            </div>
          </CollapsibleSection>

          {/* Interview & Onboarding */}
          <CollapsibleSection
            icon={<ClipboardList className="w-5 h-5" />}
            title="Interview and onboarding guidance"
          >
            <p className="text-gray-600 text-sm mb-4">
              Use the parts of this section that are relevant to where you are in the process.
            </p>

            <div className="space-y-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Questions to ask</h4>
                <ul className="space-y-2">
                  {briefingPack.interviewGuidance.questions.map((question, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-emerald-600 mt-1">•</span>
                      <p className="text-gray-700">{question}</p>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-3">Onboarding checklist</h4>
                <ul className="space-y-2">
                  {briefingPack.interviewGuidance.checklist.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        className="mt-1 w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                      />
                      <p className="text-gray-700">{item}</p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CollapsibleSection>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <button
          onClick={onStartOver}
          className="flex-1 py-3 px-4 rounded-lg font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Start Over
        </button>
        <button
          onClick={onApprove}
          className="flex-1 py-3 px-4 rounded-lg font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
        >
          Approve & Continue
        </button>
      </div>
    </div>
  )
}

function CollapsibleSection({
  icon,
  title,
  children,
  defaultOpen = false
}: {
  icon: React.ReactNode
  title: string
  children: React.ReactNode
  defaultOpen?: boolean
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="text-emerald-600">{icon}</div>
          <h3 className="font-medium text-gray-900">{title}</h3>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>
      {isOpen && <div className="px-6 pb-6">{children}</div>}
    </div>
  )
}

function BriefingListItem({ item }: { item: { content: string; source: 'report' | 'parent' } }) {
  return (
    <li className="flex items-start gap-3">
      <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mt-2 flex-shrink-0" />
      <p className="text-gray-700">{item.content}</p>
    </li>
  )
}
